import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FiLogOut, FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CreateChapter = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Theme/Category form state
  const [themeData, setThemeData] = useState({
    name: '',
    description: '',
    order_index: ''
  });

  // Chapters state - array de capítulos, cada um com seus desafios
  const [chapters, setChapters] = useState([
    {
      title: '',
      description: '',
      content: '',
      video_url: '',
      video_middle_url: '',
      video_conclusion_url: '',
      order_index: 1,
      challenges: [
        { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 1 },
        { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 2 },
        { type: 'essay', question: '', order_index: 3 }
      ]
    }
  ]);

  const handleThemeChange = (field, value) => {
    setThemeData({ ...themeData, [field]: value });
  };

  const addChapter = () => {
    const newOrderIndex = chapters.length + 1;
    setChapters([...chapters, {
      title: '',
      description: '',
      content: '',
      video_url: '',
      video_middle_url: '',
      video_conclusion_url: '',
      order_index: newOrderIndex,
      challenges: [
        { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 1 },
        { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 2 },
        { type: 'essay', question: '', order_index: 3 }
      ]
    }]);
  };

  const removeChapter = (index) => {
    if (chapters.length === 1) {
      alert('Deve haver pelo menos um capítulo!');
      return;
    }
    const updated = chapters.filter((_, i) => i !== index);
    // Reordenar
    updated.forEach((chapter, i) => {
      chapter.order_index = i + 1;
    });
    setChapters(updated);
  };

  const handleChapterChange = (chapterIndex, field, value) => {
    const updated = [...chapters];
    updated[chapterIndex] = { ...updated[chapterIndex], [field]: value };
    setChapters(updated);
  };

  const handleChallengeChange = (chapterIndex, challengeIndex, field, value) => {
    const updated = [...chapters];
    updated[chapterIndex].challenges[challengeIndex] = {
      ...updated[chapterIndex].challenges[challengeIndex],
      [field]: value
    };
    setChapters(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar tema
    if (!themeData.name || !themeData.order_index) {
      alert('Preencha nome e ordem do tema');
      return;
    }

    // Validar capítulos
    if (chapters.length === 0) {
      alert('Adicione pelo menos um capítulo');
      return;
    }

    for (let i = 0; i < chapters.length; i++) {
      const chapter = chapters[i];
      if (!chapter.title || !chapter.description || !chapter.content) {
        alert(`Preencha todos os campos obrigatórios do capítulo ${i + 1}`);
        return;
      }

      // Validar desafios de cada capítulo
      const multipleChoice = chapter.challenges.filter(c => c.type === 'multiple_choice');
      const essay = chapter.challenges.filter(c => c.type === 'essay');

      if (multipleChoice.length !== 2 || essay.length !== 1) {
        alert(`Capítulo ${i + 1}: Deve haver exatamente 2 questões de múltipla escolha e 1 questão discursiva`);
        return;
      }

      for (let j = 0; j < multipleChoice.length; j++) {
        const mc = multipleChoice[j];
        if (!mc.question || !mc.option_a || !mc.option_b || !mc.option_c || !mc.option_d || !mc.correct_answer) {
          alert(`Capítulo ${i + 1}, Questão ${j + 1}: Preencha todos os campos da questão de múltipla escolha`);
          return;
        }
      }

      if (!essay[0].question) {
        alert(`Capítulo ${i + 1}: Preencha a questão discursiva`);
        return;
      }
    }

    try {
      // Criar tema/categoria
      const categoryResponse = await axios.post(`${API_URL}/admin-master/categories`, {
        name: themeData.name,
        description: themeData.description || '',
        order_index: parseInt(themeData.order_index)
      });

      const categoryId = categoryResponse.data.category.id;

      // Criar cada capítulo e seus desafios
      for (const chapter of chapters) {
        // Criar capítulo
        const chapterResponse = await axios.post(`${API_URL}/admin-master/chapters`, {
          category_id: categoryId,
          title: chapter.title,
          description: chapter.description,
          content: chapter.content,
          video_url: chapter.video_url || '',
          video_middle_url: chapter.video_middle_url || '',
          video_conclusion_url: chapter.video_conclusion_url || '',
          order_index: chapter.order_index
        });

        const chapterId = chapterResponse.data.chapter.id;

        // Criar desafios do capítulo
        await axios.post(`${API_URL}/admin-master/chapters/${chapterId}/challenges`, {
          challenges: chapter.challenges
        });
      }

      alert(`Tema criado com sucesso! ${chapters.length} capítulo(s) adicionado(s).`);
      
      // Limpar formulário
      setThemeData({ name: '', description: '', order_index: '' });
      setChapters([{
        title: '',
        description: '',
        content: '',
        video_url: '',
        video_middle_url: '',
        video_conclusion_url: '',
        order_index: 1,
        challenges: [
          { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 1 },
          { type: 'multiple_choice', question: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: '', order_index: 2 },
          { type: 'essay', question: '', order_index: 3 }
        ]
      }]);
    } catch (error) {
      console.error('Erro ao criar tema:', error);
      alert('Erro ao criar tema: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Criar Novo Tema</h1>
            <p className="text-sm text-gray-600">Admin Master - {user?.username}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              Voltar ao Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <FiLogOut />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto max-w-4xl mx-auto w-full px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Theme Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Informações do Tema</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Tema *
                </label>
                <input
                  type="text"
                  value={themeData.name}
                  onChange={(e) => handleThemeChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Aula 06 - Projeto Final"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <input
                  type="text"
                  value={themeData.description}
                  onChange={(e) => handleThemeChange('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descrição do tema"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordem *
                </label>
                <input
                  type="number"
                  value={themeData.order_index}
                  onChange={(e) => handleThemeChange('order_index', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                  placeholder="Ordem do tema"
                  required
                />
              </div>
            </div>
          </div>

          {/* Chapters Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Capítulos</h2>
              <button
                type="button"
                onClick={addChapter}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <FiPlus />
                Adicionar Capítulo
              </button>
            </div>

            {chapters.map((chapter, chapterIndex) => (
              <div key={chapterIndex} className="mb-6 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Capítulo {chapterIndex + 1}</h3>
                  {chapters.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeChapter(chapterIndex)}
                      className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      <FiTrash2 />
                      Remover
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título *
                    </label>
                    <input
                      type="text"
                      value={chapter.title}
                      onChange={(e) => handleChapterChange(chapterIndex, 'title', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição *
                    </label>
                    <input
                      type="text"
                      value={chapter.description}
                      onChange={(e) => handleChapterChange(chapterIndex, 'description', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conteúdo (Markdown) *
                    </label>
                    <textarea
                      value={chapter.content}
                      onChange={(e) => handleChapterChange(chapterIndex, 'content', e.target.value)}
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Introdução (YouTube)
                      </label>
                      <input
                        type="url"
                        value={chapter.video_url}
                        onChange={(e) => handleChapterChange(chapterIndex, 'video_url', e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mão na Massa (YouTube)
                      </label>
                      <input
                        type="url"
                        value={chapter.video_middle_url}
                        onChange={(e) => handleChapterChange(chapterIndex, 'video_middle_url', e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conclusão e Curiosidades (YouTube)
                      </label>
                      <input
                        type="url"
                        value={chapter.video_conclusion_url}
                        onChange={(e) => handleChapterChange(chapterIndex, 'video_conclusion_url', e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Challenges for this chapter */}
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <h4 className="font-semibold text-gray-800 mb-3">Desafios (2 múltipla escolha + 1 discursiva)</h4>
                    
                    {chapter.challenges.map((challenge, challengeIndex) => (
                      <div key={challengeIndex} className="mb-4 p-3 border border-gray-200 rounded bg-white">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">
                          {challenge.type === 'essay' ? 'Questão Discursiva' : `Questão ${challengeIndex + 1} - Múltipla Escolha`}
                        </h5>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Questão *
                            </label>
                            <textarea
                              value={challenge.question}
                              onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'question', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                            />
                          </div>

                          {challenge.type === 'multiple_choice' && (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Opção A *</label>
                                  <input
                                    type="text"
                                    value={challenge.option_a}
                                    onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'option_a', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Opção B *</label>
                                  <input
                                    type="text"
                                    value={challenge.option_b}
                                    onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'option_b', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Opção C *</label>
                                  <input
                                    type="text"
                                    value={challenge.option_c}
                                    onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'option_c', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Opção D *</label>
                                  <input
                                    type="text"
                                    value={challenge.option_d}
                                    onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'option_d', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    required
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Resposta Correta *</label>
                                <select
                                  value={challenge.correct_answer}
                                  onChange={(e) => handleChallengeChange(chapterIndex, challengeIndex, 'correct_answer', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  required
                                >
                                  <option value="">Selecione</option>
                                  <option value="A">A</option>
                                  <option value="B">B</option>
                                  <option value="C">C</option>
                                  <option value="D">D</option>
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              <FiSave />
              Criar Tema
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateChapter;
